import React from 'react'
import {useTranslation, Trans} from 'react-i18next'
import {useParams, useHistory, useRouteMatch} from 'react-router-dom'

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import {Box, Typography, Theme, IconButton} from '@mui/material'
import {makeStyles} from 'tss-react/mui'

export interface ProgramSession {
  sessionIndex: string
  timeframe: string
  title: string
  description: string // RTE content from Contentful
}

export interface LearnMoreSliderProps {
  sessions: ProgramSession[]
  colors?: any // Product line colors
  initialSessionId?: string // Initial session ID from URL
}

interface ProgramInfoParams {
  program: string
  sessionid?: string
}

const useStyles = makeStyles<{colors?: any}>()((theme: Theme, {colors}) => ({
  navigationContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
  },
  card: {
    background: '#FFFFFF',
    padding: '40px 80px',
    borderRadius: '8px',
    width: '100%',
    boxShadow: '0px 3px 16px rgba(190, 192, 198, 0.24)',
  },
  chevronButton: {
    width: '50px',
    height: '50px',
    backgroundColor: colors?.secondary || '#E1F4F7',
    borderRadius: '50%',
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 1,
    '&:hover': {
      backgroundColor: colors?.secondary || '#E1F4F7',
      opacity: 0.8,
    },
  },
  chevronLeft: {
    left: '-25px',
  },
  chevronRight: {
    right: '-25px',
  },
  sessionLabel: {
    fontFamily: 'DM Sans',
    fontStyle: 'normal',
    fontWeight: 700,
    fontSize: '14px',
    lineHeight: '130%',
    letterSpacing: '0.15px',
    color: colors?.primary || '#148199',
    marginBottom: '16px',
  },
  breadcrumbContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '80px',
  },
  breadcrumbItem: {
    width: '82px',
    height: '4px',
    background: '#B6BAC8',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    '&:hover': {
      opacity: 0.8,
    },
  },
  breadcrumbItemActive: {
    background: colors?.primary || '#148199',
  },
  timeframeBadge: {
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 400,
    backgroundColor: '#F3F4F6',
    color: '#6B7280',
    display: 'inline-block',
    marginBottom: '24px',
    width: 'fit-content',
  },
  title: {
    fontSize: '24px',
    fontWeight: 600,
    color: '#251F1F',
    marginBottom: '40px',
    fontFamily: 'Poppins',
    fontStyle: 'normal',
    lineHeight: '150%',
    letterSpacing: '0.5px',
  },
  description: {
    fontFamily: 'DM Sans',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '16px',
    maxWidth: '740px',
    lineHeight: '150%',
    letterSpacing: '0.15px',
    color: '#251F1F',
    '& p': {
      margin: '0 0 0 0',
      '&:last-child': {
        marginBottom: 0,
      },
    },
    '& ul, & ol': {
      marginLeft: '0',
      paddingLeft: '18px',
      marginBottom: '16px',
    },
    '& li': {
      marginBottom: '8px',
    },
  },
}))

const LearnMoreSlider: React.FC<LearnMoreSliderProps> = ({
  sessions,
  colors,
  initialSessionId,
}) => {
  const {classes} = useStyles({colors})
  const {t} = useTranslation()
  const {sessionid} = useParams<ProgramInfoParams>()
  const history = useHistory()
  const {url} = useRouteMatch()

  // Find current session or default to initialSessionId or first session
  const currentSessionId =
    sessionid || initialSessionId || sessions[0]?.sessionIndex
  const currentSession =
    sessions.find((session) => session.sessionIndex === currentSessionId) ||
    sessions[0]

  const handleBreadcrumbClick = (sessionIndex: string) => {
    const baseUrl = url.split('/info')[0]
    const newUrl = `${baseUrl}/info/${sessionIndex}`
    // Preserve the referrer when navigating between sessions
    const currentReferrer = (history.location.state as any)?.referrer
    history.push(newUrl, {referrer: currentReferrer})
  }

  const handleChevronClick = (direction: 'prev' | 'next') => {
    const currentIndex = sessions.findIndex(
      (session) => session.sessionIndex === currentSessionId
    )

    let targetIndex
    if (direction === 'prev') {
      targetIndex = currentIndex - 1
    } else {
      targetIndex = currentIndex + 1
    }

    if (targetIndex >= 0 && targetIndex < sessions.length) {
      const targetSessionIndex = sessions[targetIndex].sessionIndex
      handleBreadcrumbClick(targetSessionIndex)
    }
  }

  // Check if previous/next sessions exist
  const currentIndex = sessions.findIndex(
    (session) => session.sessionIndex === currentSessionId
  )
  const hasPrevious = currentIndex > 0
  const hasNext = currentIndex < sessions.length - 1

  if (!currentSession) {
    return (
      <Box className={classes.card}>
        <Typography>
          <Trans i18nKey='consultations_learn_more_slider_no_session_data_available' />
        </Typography>
      </Box>
    )
  }

  return (
    <Box className={classes.navigationContainer}>
      {/* Previous Button */}
      {hasPrevious && (
        <IconButton
          className={`${classes.chevronButton} ${classes.chevronLeft}`}
          onClick={() => handleChevronClick('prev')}
        >
          <ChevronLeftIcon sx={{color: colors?.primary || '#148199'}} />
        </IconButton>
      )}

      {/* Main Card */}
      <Box className={classes.card}>
        {/* Session Label */}
        {sessions?.length > 1 && (
          <Typography className={classes.sessionLabel}>
            {t('consultations_session_booking_booking_breadcrumb_session')}{' '}
            {currentSessionId}
          </Typography>
        )}

        {sessions?.length > 1 && (
          <Box className={classes.breadcrumbContainer}>
            {sessions.map((session) => {
              const isActive = session.sessionIndex === currentSessionId
              return (
                <Box
                  key={session.sessionIndex}
                  className={`${classes.breadcrumbItem} ${
                    isActive ? classes.breadcrumbItemActive : ''
                  }`}
                  onClick={() => handleBreadcrumbClick(session.sessionIndex)}
                />
              )
            })}
          </Box>
        )}

        {/* Session Content */}
        <Box>
          {/* Timeframe Badge */}
          {currentSession.timeframe && (
            <Typography className={classes.timeframeBadge}>
              {currentSession.timeframe}
            </Typography>
          )}

          {/* Title */}
          <Typography className={classes.title}>
            {currentSession.title}
          </Typography>

          {/* RTE Description */}
          <Box
            className={classes.description}
            // This can't be fixed with i18n.
            dangerouslySetInnerHTML={{__html: currentSession.description}}
          />
        </Box>
      </Box>

      {/* Next Button */}
      {hasNext && (
        <IconButton
          className={`${classes.chevronButton} ${classes.chevronRight}`}
          onClick={() => handleChevronClick('next')}
        >
          <ChevronRightIcon sx={{color: colors?.primary || '#148199'}} />
        </IconButton>
      )}
    </Box>
  )
}

export default LearnMoreSlider
